const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

// Tracks the single active observer so a facet re-render (or any repeated call) can release
// its references to now-detached elements instead of leaking a new observer on top of it.
let scrollAnimationObserver = null;

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  if (scrollAnimationObserver) scrollAnimationObserver.disconnect();

  scrollAnimationObserver = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => scrollAnimationObserver.observe(element));
}

// Zoom in animation logic
const ZOOM_SCALE_AMOUNT = 0.2 / 100;

// A single IntersectionObserver tracks which zoom elements are onscreen, and a single
// rAF-throttled scroll listener updates only those elements - instead of one observer and
// one scroll listener per element, which multiplied main-thread work with each zoom block.
const zoomAnimationState = {
  observer: null,
  visibleElements: new Set(),
  frameRequested: false,
};

function updateZoomRatio(element) {
  element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_SCALE_AMOUNT * percentageSeen(element));
}

function updateVisibleZoomRatios() {
  zoomAnimationState.frameRequested = false;
  zoomAnimationState.visibleElements.forEach(updateZoomRatio);
}

function onZoomScroll() {
  if (zoomAnimationState.frameRequested || zoomAnimationState.visibleElements.size === 0) return;
  zoomAnimationState.frameRequested = true;
  requestAnimationFrame(updateVisibleZoomRatios);
}

function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  if (!zoomAnimationState.observer) {
    zoomAnimationState.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          zoomAnimationState.visibleElements.add(entry.target);
        } else {
          zoomAnimationState.visibleElements.delete(entry.target);
        }
      });
    });

    window.addEventListener('scroll', onZoomScroll, { passive: true });
  }

  animationTriggerElements.forEach((element) => {
    zoomAnimationState.observer.observe(element);
    updateZoomRatio(element);
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
